import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAPISocketMessages } from 'shared/api/APISocketMessages';
import { LocalStore, useLocalStore } from 'shared/helpers/Store';
import { useFlag } from 'shared/settings/FeatureToggles';
import {
  SHIPMENT_COUNT_QUERY_KEY,
  SHIPMENT_LIST_QUERY_KEY,
  SHIPMENTS_LAST_UPDATED_QUERY_KEY,
  useShipmentsLastUpdated,
} from './VehiclesAPI';
import { ShipmentsLastUpdatedDTO } from './VehiclesDTO';

const SHIPMENTS_UPDATED_SEEN_AT_KEY = 'shipments_updated_seen_at';
const SHIPMENTS_UPDATED_MESSAGE_TYPE = 'shipments_updated';

export function hasUnseenShipmentUpdates(
  updatedAt: null | string | undefined,
  seenAt: null | string | undefined,
): boolean {
  const updatedTime = updatedAt ? Date.parse(updatedAt) : Number.NaN;

  if (Number.isNaN(updatedTime)) {
    return false;
  }

  const seenTime = seenAt ? Date.parse(seenAt) : Number.NaN;

  return Number.isNaN(seenTime) || updatedTime > seenTime;
}

export function useShipmentsUpdatedIndicator(): {
  isIndicatorVisible: boolean;
} {
  const queryClient = useQueryClient();
  const isVehiclesPageEnabled = useFlag('ctms_edi_vehicles');
  const seenAt = useLocalStore(SHIPMENTS_UPDATED_SEEN_AT_KEY);
  const { data } = useShipmentsLastUpdated({ enabled: isVehiclesPageEnabled });

  useAPISocketMessages<ShipmentsLastUpdatedDTO>((payload) => {
    if (
      !isVehiclesPageEnabled ||
      payload.meta.type !== SHIPMENTS_UPDATED_MESSAGE_TYPE
    ) {
      return;
    }

    queryClient.setQueryData<ShipmentsLastUpdatedDTO>(
      SHIPMENTS_LAST_UPDATED_QUERY_KEY,
      payload.data,
    );

    void queryClient.invalidateQueries({ queryKey: SHIPMENT_LIST_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: SHIPMENT_COUNT_QUERY_KEY });
  });

  return {
    isIndicatorVisible: hasUnseenShipmentUpdates(
      data?.shipments_updated_at,
      seenAt,
    ),
  };
}

export function useMarkShipmentUpdatesAsSeen(): void {
  const { data } = useShipmentsLastUpdated();
  const updatedAt = data?.shipments_updated_at;

  useEffect(() => {
    if (updatedAt) {
      LocalStore.set(SHIPMENTS_UPDATED_SEEN_AT_KEY, updatedAt);
    }
  }, [updatedAt]);
}
